import { produce } from 'immer';
import * as actions from 'store/actions';
import { ChangesState } from 'store/changes/types';
import { createReducer } from 'typesafe-actions';

const initialState: ChangesState = {
  assets: {},
};

export const changesReducer = createReducer(initialState)
  .handleAction(actions.SIGN_OUT.success, () => initialState)
  .handleAction(actions.SET_ASSET_DELTA, (state, action) =>
    produce(state, (draft) => {
      const newDelta = action.payload.delta;
      if (draft.assets[action.payload.id] === undefined) {
        draft.assets[action.payload.id] = { change_timestamp: 0 };
      }
      const draftDelta = draft.assets[action.payload.id]!;
      draftDelta.change_timestamp = Date.now();
      if (newDelta.details !== undefined) {
        draftDelta.details = draftDelta.details
          ? { ...draftDelta.details, ...newDelta.details }
          : newDelta.details;
      }
      if (newDelta.files !== undefined) {
        Object.entries(newDelta.files).forEach(([id, item]) => {
          if (!draftDelta.files) {
            draftDelta.files = {};
          }
          draftDelta.files[id] = item;
        });
      }
      if (newDelta.plans !== undefined) {
        Object.entries(newDelta.plans).forEach(([id, item]) => {
          if (!draftDelta.plans) {
            draftDelta.plans = {};
          }
          draftDelta.plans[id] = item;
        });
      }
      if (newDelta.fieldResults !== undefined) {
        Object.entries(newDelta.fieldResults).forEach(([id, item]) => {
          if (!draftDelta.fieldResults) {
            draftDelta.fieldResults = {};
          }
          draftDelta.fieldResults[id] = item;
        });
      }
      if (newDelta.fieldInstances !== undefined) {
        Object.entries(newDelta.fieldInstances).forEach(([id, item]) => {
          if (!draftDelta.fieldInstances) {
            draftDelta.fieldInstances = {};
          }
          draftDelta.fieldInstances[id] = item;
        });
      }
      // Assessment Delta Changes
      if (newDelta.assessments !== undefined) {
        Object.entries(newDelta.assessments).forEach(
          ([assessmentId, assessment]) => {
            if (!draftDelta.assessments) {
              draftDelta.assessments = { [assessmentId]: assessment };
            } else if (!assessment) {
              draftDelta.assessments[assessmentId] = assessment;
            } else {
              const draftAssessment = draftDelta.assessments[assessmentId];
              if (!draftAssessment) {
                draftDelta.assessments[assessmentId] = assessment;
              } else {
                if (assessment.asset !== undefined) {
                  draftAssessment.asset = assessment.asset;
                }
                if (assessment.details !== undefined) {
                  draftAssessment.details = assessment.details;
                }
                if (assessment.sections !== undefined) {
                  Object.entries(assessment.sections).forEach(([id, item]) => {
                    if (!draftAssessment.sections) {
                      draftAssessment.sections = {};
                    }
                    draftAssessment.sections[id] = item;
                  });
                }
                if (assessment.questions !== undefined) {
                  Object.entries(assessment.questions).forEach(([id, item]) => {
                    if (!draftAssessment.questions) {
                      draftAssessment.questions = {};
                    }
                    draftAssessment.questions[id] = item;
                  });
                }
                if (assessment.questionComments !== undefined) {
                  Object.entries(assessment.questionComments).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.questionComments) {
                        draftAssessment.questionComments = {};
                      }
                      draftAssessment.questionComments[id] = item;
                    },
                  );
                }
                if (assessment.checklistItems !== undefined) {
                  Object.entries(assessment.checklistItems).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.checklistItems) {
                        draftAssessment.checklistItems = {};
                      }
                      draftAssessment.checklistItems[id] = item;
                    },
                  );
                }
                if (assessment.controls !== undefined) {
                  Object.entries(assessment.controls).forEach(([id, item]) => {
                    if (!draftAssessment.controls) {
                      draftAssessment.controls = {};
                    }
                    draftAssessment.controls[id] = item;
                  });
                }
                if (assessment.actions !== undefined) {
                  Object.entries(assessment.actions).forEach(([id, item]) => {
                    if (!draftAssessment.actions) {
                      draftAssessment.actions = {};
                    }
                    draftAssessment.actions[id] = item;
                  });
                }
                if (assessment.fieldInstances !== undefined) {
                  Object.entries(assessment.fieldInstances).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.fieldInstances) {
                        draftAssessment.fieldInstances = {};
                      }
                      const fieldInstance = draftAssessment.fieldInstances[id];
                      draftAssessment.fieldInstances[id] = fieldInstance
                        ? {
                            ...item,
                            results: {
                              ...fieldInstance.results,
                              ...item.results,
                            },
                          }
                        : item;
                    },
                  );
                }
                if (assessment.fieldInstanceChecks !== undefined) {
                  Object.entries(assessment.fieldInstanceChecks).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.fieldInstanceChecks) {
                        draftAssessment.fieldInstanceChecks = {};
                      }
                      draftAssessment.fieldInstanceChecks[id] = item;
                    },
                  );
                }
                if (assessment.fieldResults !== undefined) {
                  Object.entries(assessment.fieldResults).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.fieldResults) {
                        draftAssessment.fieldResults = {};
                      }
                      draftAssessment.fieldResults[id] = item;
                    },
                  );
                }
                if (assessment.findingSuggestions !== undefined) {
                  Object.entries(assessment.findingSuggestions).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.findingSuggestions) {
                        draftAssessment.findingSuggestions = {};
                      }
                      draftAssessment.findingSuggestions[id] = item;
                    },
                  );
                }
                if (assessment.signatories !== undefined) {
                  Object.entries(assessment.signatories).forEach(
                    ([id, item]) => {
                      if (!draftAssessment.signatories) {
                        draftAssessment.signatories = {};
                      }
                      draftAssessment.signatories[id] = item;
                    },
                  );
                }
              }
            }
          },
        );
      }
      if (Object.keys(draftDelta).length === 1) {
        // Only one key (change_timestamp), remove this change
        delete draft.assets[action.payload.id];
      }
    }),
  )
  .handleAction(actions.DELETE_ASSETS, (state, action) =>
    produce(state, (draft) =>
      action.payload.ids.forEach((assetId) => {
        delete draft.assets[assetId];
      }),
    ),
  )
  .handleAction(actions.CLEAR_LOCAL_CHANGES, (state, action) =>
    produce(state, (draft) => {
      const { timestamp } = action.payload;
      const assetErrors = action.payload.asset_errors;

      for (const assetId of Object.keys(draft.assets)) {
        // Safe to delete if the changes were made BEFORE sync started
        if (draft.assets[assetId]!.change_timestamp <= timestamp) {
          const errors = assetErrors?.[assetId];
          if (!errors) {
            // Remove asset changes as there are no errors for this asset
            delete draft.assets[assetId];
          } else if (!errors.asset) {
            // "asset" error: an issue with entire asset, if that doesn't exist
            // there are individual errors with sub-items

            for (const changeKey of Object.keys(draft.assets[assetId]!)) {
              if (changeKey === 'change_timestamp') {
                // Do nothing with change_timestamp key
              } else if (
                !Object.prototype.hasOwnProperty.call(errors, changeKey)
              ) {
                // There's no error for this change, so delete it
                // @ts-expect-error We know changeKey exists
                delete draft.assets[assetId]?.[changeKey];
              } else if (changeKey === 'assessments') {
                // Assessment errors come as sub-keys for each assessment
                // Check each and only delete if there is no errors for it
                const assessmentIds = Object.keys(
                  draft.assets[assetId]!.assessments || {},
                );

                for (const assessmentId of assessmentIds) {
                  if (!errors.assessments?.[assessmentId]) {
                    delete draft.assets[assetId]!.assessments?.[assessmentId];
                  }
                }
              }
            }
          }
        }
      }
    }),
  )
  .handleAction(actions.CLEAR_LOCAL_ASSET_CHANGES, (state, action) =>
    produce(state, (draft) => {
      const { assetId, assetKeyId, assessmentId } = action.payload;
      if (assessmentId) {
        // Delete an assessment
        delete draft.assets[assetId]?.assessments?.[assessmentId];
        if (
          Object.keys(draft.assets[assetId]?.assessments || {}).length === 0
        ) {
          delete draft.assets[assetId]?.assessments;
        }
      } else if (assetKeyId) {
        // Delete specific change
        delete draft.assets[assetId]?.[assetKeyId];
      } else {
        // Delete all changes
        delete draft.assets[assetId];
      }
      // Delete asset if no remaining changes
      if (Object.keys(draft.assets[assetId] || {}).length === 0) {
        delete draft.assets[assetId];
      }
    }),
  );

// .handleAction(actions.UPGRADE, (state) =>
//   produce(state, (draft) => {
//     if (!draft.dv) {
//       draft.dv = 0;
//     }
//   }),
// );
